import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Download, History, Wallet as WalletIcon, CreditCard, ArrowUpRight } from 'lucide-react';
import { usePaystackPayment } from 'react-paystack';
import { useWallet, useTransactions, useWithdrawals, usePayoutSettings, useWithdraw, useDeletePayoutSetting, useFundWallet } from '../../hooks/useWallet';
import { Container } from '../../components/layout/Container';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { Modal } from '../../components/common/Modal';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { WITHDRAWAL_STATUS_COLORS } from '../../utils/constants';
import type { Transaction, Withdrawal, WithdrawalStatus } from '../../types';
import toast from 'react-hot-toast';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';

type TabType = 'overview' | 'transactions' | 'withdrawals';

export const Wallet: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionType, setTransactionType] = useState<string>('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date(); date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [withdrawalStatus, setWithdrawalStatus] = useState<string>('');
  const [withdrawalStartDate, setWithdrawalStartDate] = useState(() => {
    const date = new Date(); date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [withdrawalEndDate, setWithdrawalEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: walletData, isLoading: walletLoading } = useWallet();
  const { data: recentTransactionsData } = useTransactions({ limit: 10 });
  const transactionStartUTC = startDate ? new Date(startDate + 'T00:00:00').toISOString() : undefined;
  const transactionEndUTC = endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined;
  const { data: allTransactionsData } = useTransactions({ page: transactionPage, limit: 20, type: transactionType || undefined, startDate: transactionStartUTC, endDate: transactionEndUTC });
  const { data: recentWithdrawalsData } = useWithdrawals({ limit: 10 });
  const withdrawalStartUTC = withdrawalStartDate ? new Date(withdrawalStartDate + 'T00:00:00').toISOString() : undefined;
  const withdrawalEndUTC = withdrawalEndDate ? new Date(withdrawalEndDate + 'T23:59:59').toISOString() : undefined;
  const { data: allWithdrawalsData } = useWithdrawals({ page: withdrawalPage, limit: 20, status: withdrawalStatus || undefined, startDate: withdrawalStartUTC, endDate: withdrawalEndUTC });
  const { data: payoutSettingsData } = usePayoutSettings();
  const withdraw = useWithdraw();
  const deletePayoutSetting = useDeletePayoutSetting();
  const fundWallet = useFundWallet();

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawFee, setWithdrawFee] = useState<{ fee: number; net: number } | null>(null);
  const [fundAmount, setFundAmount] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('PAYSTACK');
  const [selectedPayoutId, setSelectedPayoutId] = useState('');

  const PAYSTACK_PUBLIC_KEY = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
  const initializePaystack = usePaystackPayment({ publicKey: PAYSTACK_PUBLIC_KEY });

  const wallet = walletData?.data;
  const recentTransactions = recentTransactionsData?.data?.transactions || [];
  const allTransactions = allTransactionsData?.data?.transactions || [];
  const transactionsPagination = allTransactionsData?.data?.pagination;
  const recentWithdrawals = recentWithdrawalsData?.data?.withdrawals || [];
  const allWithdrawals = allWithdrawalsData?.data?.withdrawals || [];
  const withdrawalsPagination = allWithdrawalsData?.data?.pagination;
  const payoutSettings = payoutSettingsData?.data || [];

  // Calculate withdrawal fee
  useEffect(() => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) { setWithdrawFee(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/wallet/withdrawal-fee', { params: { amount: amt } });
        setWithdrawFee({ fee: res.data.data.fee, net: res.data.data.net });
      } catch { setWithdrawFee(null); }
    }, 300);
    return () => clearTimeout(timer);
  }, [withdrawAmount]);

  const handleWithdraw = async () => {
    await withdraw.mutateAsync({ amount: parseFloat(withdrawAmount), payoutSettingId: selectedPayoutId });
    setShowWithdrawModal(false); setWithdrawAmount(''); setSelectedPayoutId('');
  };

  const verifyPayment = useCallback(async (reference: string) => {
    try {
      await api.post('/webhooks/paystack/verify', { reference });
      toast.success('Wallet funded successfully!');
      window.location.reload();
    } catch {
      toast.success('Payment received. It may take a moment to process.');
    }
  }, []);

  const handleFund = async () => {
    const response = await fundWallet.mutateAsync({ amount: parseFloat(fundAmount), paymentProvider });
    const paymentData = response.data;
    setShowFundModal(false);

    // Paystack: use inline popup
    if (paymentProvider === 'PAYSTACK' && paymentData?.reference) {
      initializePaystack({
        config: {
          email: user?.email || '',
          amount: parseFloat(fundAmount) * 100, // react-paystack expects kobo/cents
          reference: paymentData.reference,
          currency: wallet?.currency || 'USD',
        },
        onSuccess: (ref: any) => {
          const reference = ref?.reference || paymentData.reference;
          verifyPayment(reference);
        },
        onClose: () => toast('Payment cancelled', { icon: 'ℹ️' }),
      });
      setFundAmount('');
      return;
    }

    // Stripe/PayPal: redirect fallback
    const paymentUrl = paymentData?.authorizationUrl || paymentData?.url;
    if (paymentUrl) {
      window.location.href = paymentUrl;
    } else {
      toast.success('Payment initialized successfully!');
    }
    setFundAmount('');
  };

  const handleFilterReset = () => {
    setTransactionType('');
    const date = new Date(); date.setMonth(date.getMonth() - 1);
    setStartDate(date.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setTransactionPage(1);
  };

  const handleWithdrawalFilterReset = () => {
    setWithdrawalStatus('');
    const date = new Date(); date.setMonth(date.getMonth() - 1);
    setWithdrawalStartDate(date.toISOString().split('T')[0]);
    setWithdrawalEndDate(new Date().toISOString().split('T')[0]);
    setWithdrawalPage(1);
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: WalletIcon },
    { id: 'transactions' as TabType, label: 'All Transactions', icon: History },
    { id: 'withdrawals' as TabType, label: 'All Withdrawals', icon: Download },
  ];

  if (walletLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Wallet</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <Card className="relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 dark:bg-indigo-950 rounded-full -mr-8 -mt-8" />
                <div className="relative">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Available Balance</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-5">
                    {wallet ? formatCurrency(wallet.balance, wallet.currency) : '—'}
                  </p>
                  <div className="space-y-2">
                    <Button fullWidth onClick={() => setShowFundModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />Fund Wallet
                    </Button>
                    <Button fullWidth variant="outline" onClick={() => setShowWithdrawModal(true)} disabled={!wallet || wallet.balance <= 0}>
                      <Download className="h-4 w-4 mr-2" />Withdraw
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Payout Settings</h3>
                  </div>
                  <Button size="sm" onClick={() => setShowPayoutModal(true)}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add
                  </Button>
                </div>
                {payoutSettings.length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-center py-6 text-sm">No payout methods configured</p>
                ) : (
                  <div className="space-y-2">
                    {payoutSettings.map((setting) => (
                      <div key={setting.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                        <div>
                          <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{setting.method === 'BANK_TRANSFER' ? 'Bank Transfer' : 'PayPal'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {setting.method === 'BANK_TRANSFER' ? `${setting.bankName} - ${setting.accountNumber}` : setting.paypalEmail}
                          </p>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => deletePayoutSetting.mutateAsync(setting.id)}>Remove</Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Transactions</h2>
                  <button onClick={() => setActiveTab('transactions')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                    View all <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <Card>
                  {recentTransactions.length === 0 ? (
                    <div className="text-center py-10">
                      <History className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-gray-400 dark:text-gray-500 text-sm">No transactions yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50 dark:divide-gray-800">
                      {recentTransactions.map((transaction: Transaction) => (
                        <div key={transaction.id} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{transaction.description}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(transaction.createdAt)}</p>
                            </div>
                            <span className={`text-sm font-bold ${transaction.type === 'DEBIT' ? 'text-red-600' : 'text-emerald-600'}`}>
                              {transaction.type === 'DEBIT' ? '-' : '+'}{formatCurrency(transaction.amount, wallet?.currency || 'USD')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Withdrawal History</h2>
                  <button onClick={() => setActiveTab('withdrawals')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                    View all <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <Card>
                  {recentWithdrawals.length === 0 ? (
                    <div className="text-center py-10">
                      <Download className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-gray-400 dark:text-gray-500 text-sm">No withdrawals yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50 dark:divide-gray-800">
                      {recentWithdrawals.map((withdrawal: Withdrawal) => (
                        <div key={withdrawal.id} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant={WITHDRAWAL_STATUS_COLORS[withdrawal.status as WithdrawalStatus]}>{withdrawal.status}</Badge>
                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(withdrawal.amount, wallet?.currency || 'USD')}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {withdrawal.payoutSetting.method === 'BANK_TRANSFER'
                              ? `${withdrawal.payoutSetting.bankName} - ${withdrawal.payoutSetting.accountNumber}`
                              : withdrawal.payoutSetting.paypalEmail}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(withdrawal.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </>
        )}

        {activeTab === 'transactions' && (
          <div>
            <Card className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">Filter Transactions</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select label="Type" value={transactionType} onChange={(e) => setTransactionType(e.target.value)}
                  options={[{ value: '', label: 'All Types' }, { value: 'CREDIT', label: 'Credit' }, { value: 'DEBIT', label: 'Debit' }]} />
                <Input label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <Input label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                <div className="flex items-start gap-2 md:mt-6">
                  <Button fullWidth onClick={() => setTransactionPage(1)}>Apply</Button>
                  <Button variant="outline" onClick={handleFilterReset}>Reset</Button>
                </div>
              </div>
            </Card>

            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Transaction History</h2>
              {allTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400 dark:text-gray-500 text-sm">No transactions found</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {allTransactions.map((transaction: Transaction) => (
                      <div key={transaction.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={transaction.type === 'CREDIT' ? 'success' : 'danger'}>{transaction.type}</Badge>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{transaction.description}</span>
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(transaction.createdAt)}</p>
                        </div>
                        <span className={`text-lg font-bold ${transaction.type === 'DEBIT' ? 'text-red-600' : 'text-emerald-600'}`}>
                          {transaction.type === 'DEBIT' ? '-' : '+'}{formatCurrency(transaction.amount, wallet?.currency || 'USD')}
                        </span>
                      </div>
                    ))}
                  </div>
                  {transactionsPagination && transactionsPagination.pages > 1 && (
                    <div className="flex justify-center items-center gap-4 mt-6">
                      <Button variant="outline" disabled={transactionPage === 1} onClick={() => setTransactionPage(transactionPage - 1)}>Previous</Button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Page {transactionPage} of {transactionsPagination.pages}</span>
                      <Button variant="outline" disabled={transactionPage === transactionsPagination.pages} onClick={() => setTransactionPage(transactionPage + 1)}>Next</Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'withdrawals' && (
          <div>
            <Card className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">Filter Withdrawals</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select label="Status" value={withdrawalStatus} onChange={(e) => setWithdrawalStatus(e.target.value)}
                  options={[
                    { value: '', label: 'All Statuses' }, { value: 'PENDING', label: 'Pending' },
                    { value: 'PROCESSING', label: 'Processing' }, { value: 'COMPLETED', label: 'Completed' },
                    { value: 'FAILED', label: 'Failed' }, { value: 'CANCELLED', label: 'Cancelled' },
                  ]} />
                <Input label="Start Date" type="date" value={withdrawalStartDate} onChange={(e) => setWithdrawalStartDate(e.target.value)} />
                <Input label="End Date" type="date" value={withdrawalEndDate} onChange={(e) => setWithdrawalEndDate(e.target.value)} />
                <div className="flex items-start gap-2 md:mt-6">
                  <Button fullWidth onClick={() => setWithdrawalPage(1)}>Apply</Button>
                  <Button variant="outline" onClick={handleWithdrawalFilterReset}>Reset</Button>
                </div>
              </div>
            </Card>

            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Withdrawal History</h2>
              {allWithdrawals.length === 0 ? (
                <div className="text-center py-12">
                  <Download className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400 dark:text-gray-500 text-sm">No withdrawals found</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {allWithdrawals.map((withdrawal: Withdrawal) => (
                      <div key={withdrawal.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={WITHDRAWAL_STATUS_COLORS[withdrawal.status as WithdrawalStatus]}>{withdrawal.status}</Badge>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {withdrawal.payoutSetting.method === 'BANK_TRANSFER' ? 'Bank Transfer' : 'PayPal'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {withdrawal.payoutSetting.method === 'BANK_TRANSFER'
                              ? `${withdrawal.payoutSetting.bankName} - ${withdrawal.payoutSetting.accountNumber}`
                              : withdrawal.payoutSetting.paypalEmail}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(withdrawal.createdAt)}</p>
                        </div>
                        <span className="text-lg font-bold text-red-600">
                          -{formatCurrency(withdrawal.amount, wallet?.currency || 'USD')}
                        </span>
                      </div>
                    ))}
                  </div>
                  {withdrawalsPagination && withdrawalsPagination.pages > 1 && (
                    <div className="flex justify-center items-center gap-4 mt-6">
                      <Button variant="outline" disabled={withdrawalPage === 1} onClick={() => setWithdrawalPage(withdrawalPage - 1)}>Previous</Button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Page {withdrawalPage} of {withdrawalsPagination.pages}</span>
                      <Button variant="outline" disabled={withdrawalPage === withdrawalsPagination.pages} onClick={() => setWithdrawalPage(withdrawalPage + 1)}>Next</Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}

        {/* Modals */}
        <Modal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} title="Withdraw Funds">
          <div className="space-y-4">
            <Input label="Amount" type="number" step="0.01" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="Enter amount" />
            {withdrawFee && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Fee</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(withdrawFee.fee, wallet?.currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">You'll receive</span>
                  <span className="font-bold text-emerald-600">{formatCurrency(withdrawFee.net, wallet?.currency)}</span>
                </div>
              </div>
            )}
            <Select label="Payout Method" value={selectedPayoutId} onChange={(e) => setSelectedPayoutId(e.target.value)}
              options={[
                { value: '', label: 'Select payout method' },
                ...payoutSettings.map((s) => ({
                  value: s.id,
                  label: s.method === 'BANK_TRANSFER' ? `${s.bankName} - ${s.accountNumber}` : s.paypalEmail!,
                })),
              ]} />
            <Button fullWidth onClick={handleWithdraw} isLoading={withdraw.isPending} disabled={!withdrawAmount || !selectedPayoutId}>
              Confirm Withdrawal
            </Button>
          </div>
        </Modal>

        <Modal isOpen={showPayoutModal} onClose={() => setShowPayoutModal(false)} title="Add Payout Method">
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Configure your payout settings in the Settings page.</p>
          <Button fullWidth onClick={() => { setShowPayoutModal(false); window.location.href = '/settings'; }}>
            Go to Settings
          </Button>
        </Modal>

        <Modal isOpen={showFundModal} onClose={() => setShowFundModal(false)} title="Fund Wallet">
          <div className="space-y-4">
            <Input label="Amount" type="number" value={fundAmount} onChange={(e) => setFundAmount(e.target.value)} placeholder="Enter amount" min="1" required />
            <Select label="Payment Method" value={paymentProvider} onChange={(e) => setPaymentProvider(e.target.value)}
              options={[{ value: 'PAYSTACK', label: 'Paystack' }, { value: 'STRIPE', label: 'Stripe' }, { value: 'PAYPAL', label: 'PayPal' }]} />
            <Button fullWidth onClick={handleFund} isLoading={fundWallet.isPending} disabled={!fundAmount || parseFloat(fundAmount) <= 0}>
              Continue to Payment
            </Button>
          </div>
        </Modal>
      </Container>
    </div>
  );
};
